import { LiveSignal } from "@/types/api";
import { lifecycleOf } from "./resultPresentation";

// Normalized match lifecycle derived STRICTLY from the real fields the upstream
// ATLAS feed exposes today (status: PENDING|RESOLVED, minute, resolution).
// The full provider lifecycle (SCHEDULED → LIVE_1H → HT → LIVE_2H → FT →
// RESOLVING → RESOLVED, plus ET/PEN/suspensions) requires the upstream SDIP
// Live service; we NEVER fabricate HT/ET/suspension transitions we can't observe.
export type LiveState = "SCHEDULED" | "LIVE_1H" | "LIVE_2H" | "LIVE" | "FT" | "RESOLVED";

export interface LiveStatePresentation {
  state: LiveState;
  tr: string;
  en: string;
  isLive: boolean;
}

export function liveStateOf(s: Pick<LiveSignal, "status" | "won" | "minute" | "resolved_at" | "final_home_goals" | "final_away_goals">): LiveStatePresentation {
  const key = lifecycleOf(s.status, s.won, {
    resolved_at: s.resolved_at, final_home_goals: s.final_home_goals, final_away_goals: s.final_away_goals,
  });
  if (key !== "PENDING") return { state: "RESOLVED", tr: "Bitti", en: "Full time", isLive: false };
  const m = s.minute;
  if (m == null) return { state: "SCHEDULED", tr: "Canlı", en: "Live", isLive: true };
  if (m <= 45) return { state: "LIVE_1H", tr: "1. Yarı", en: "1st half", isLive: true };
  return { state: "LIVE_2H", tr: "2. Yarı", en: "2nd half", isLive: true };
}

// Current-match presentation values. The live overlay (live_minute / live_score_*
// / live_status) is the authoritative in-play state and ALWAYS wins over the
// signal snapshot (minute / initial_* / status). Fallback chain is tolerant of
// both flat and nested upstream shapes. 0 is a valid live value (?? only falls
// through on null/undefined), so an advanced 0-0 is never downgraded to a stale
// snapshot score.
export function displayMinute(s: LiveSignal): number | null {
  const r = s as any;
  return r.live_minute ?? r.live?.minute ?? r.liveNow?.minute ?? r.minute ?? null;
}
export function displayStatus(s: LiveSignal): string | null {
  const r = s as any;
  return r.live_status ?? r.live?.status ?? r.liveNow?.status ?? r.status ?? null;
}
export function displayHomeScore(s: LiveSignal): number | null {
  const r = s as any;
  return r.live_score_home ?? r.live?.score?.home ?? r.score?.home ?? r.home_goals ?? r.initial_home_goals ?? null;
}
export function displayAwayScore(s: LiveSignal): number | null {
  const r = s as any;
  return r.live_score_away ?? r.live?.score?.away ?? r.score?.away ?? r.away_goals ?? r.initial_away_goals ?? null;
}

// A fixture is "currently live" (ATLAS-eligible LIVE NOW) when it has an active
// (pending) signal with an in-play minute.
export function isLiveNow(s: Pick<LiveSignal, "status" | "won" | "minute" | "resolved_at" | "final_home_goals" | "final_away_goals">): boolean {
  return liveStateOf(s).isLive && s.minute != null;
}

// CANONICAL current-state merge for list cards. Authoritative current-state
// (live-now batch OR per-fixture live-center — both expose minute/home_goals/
// away_goals) ALWAYS wins over the signal snapshot. The original signal minute
// is returned separately as immutable historical metadata. Resolved signals use
// their settled final score. No fabrication: 0 is a valid live value.
export function currentStateOf(
  s: LiveSignal,
  live: { minute?: number | null; home_goals?: number | null; away_goals?: number | null } | null | undefined,
  resolved: boolean,
): { minute: number | null; home: number | null; away: number | null; signalMinute: number | null } {
  return {
    minute: live?.minute ?? displayMinute(s),
    home: resolved ? s.final_home_goals : (live?.home_goals ?? displayHomeScore(s)),
    away: resolved ? s.final_away_goals : (live?.away_goals ?? displayAwayScore(s)),
    signalMinute: s.minute ?? null,
  };
}
